import isApiError from "@insite/client-framework/Common/isApiError";
import { createHandlerChainRunner, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import translate from "@insite/client-framework/Translate";
import { getTerms } from "@tKgpco/Services/TermsService";
import { CustomApiHandler } from "@tKgpco/Store/CustomHandlerCreator";
import { QuoteTerm, QuoteTermsFormValues, QuoteTermState } from "@tKgpco/Store/Data/Terms/TermsState";

type Parameters = { queryParams: Partial<QuoteTermsFormValues>, onSuccess: () => void } & HasOnSuccess & HasOnError;

type HandlerType = CustomApiHandler<Parameters, QuoteTermState>;

export const BeginExportTerm: HandlerType = props => {
    props.dispatch({ type: "Custom/Terms/ShowOverlay" });
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await getTerms(props.parameter.queryParams);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }
        throw error;
    }
};

export const ExportData: HandlerType = async props => {
    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");

    const data: any[] = [];
    const state = props.getState();
    const language = state.context.session.language;
    const termData = (term: QuoteTerm) => {
        return [
            term.termsCode,
            term.vendor,
            term.type,
            term.estimate,
            term.default,
            term.description,
            term.source,
            term.id,
        ];
    };

    if (props.apiResult) {
        props.apiResult?.quoteTerms.forEach((acc: QuoteTerm) => {
            data.push(termData(acc));
        });
    }
    // TermsCode	Vendor	Type    Estimate	Description	Default	Id
    const documentData = {
        fields: [
            translate("TermsCode"),
            translate("Vendor"),
            translate("Type"),
            translate("Estimate"),
            translate("Description"),
            translate("Default"),
            translate("Source"),
            translate("Id"),
        ],
        data,
    };

    const csv = unparse(documentData);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });

    const fileName = `terms_table.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const CompleteExportTerm: HandlerType = props => {
    props.dispatch({ type: "Custom/Terms/HideOverlay" });
};

export const chain = [BeginExportTerm, RequestDataFromApi, ExportData, CompleteExportTerm];

const exportTerms = createHandlerChainRunner(chain, "ExportTerms");
export default exportTerms;
